import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ScrollText, RefreshCw, Loader2, Trash2, Pencil, UserPlus, UserMinus, ShieldCheck, AtSign, Settings2, Hash, Server, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

const EVENTS = {
  message_delete: { label: 'Message deleted', chip: 'Deletes', icon: Trash2, cls: 'text-red-400 bg-red-500/10 border-red-500/20' },
  message_edit: { label: 'Message edited', chip: 'Edits', icon: Pencil, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  member_join: { label: 'Member joined', chip: 'Joins', icon: UserPlus, cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  member_leave: { label: 'Member left', chip: 'Leaves', icon: UserMinus, cls: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' },
  role_change: { label: 'Roles changed', chip: 'Roles', icon: ShieldCheck, cls: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
  nickname_change: { label: 'Nickname changed', chip: 'Nicknames', icon: AtSign, cls: 'text-sky-400 bg-sky-500/10 border-sky-500/20' },
};
const EVENT_KEYS = Object.keys(EVENTS);
const DEFAULT_EVENTS = EVENT_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {});

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return '';
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

const asText = (v) => (v == null ? '' : typeof v === 'string' ? v : Array.isArray(v) ? v.join(', ') : JSON.stringify(v));

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-40 ${checked ? 'bg-[#5865F2]' : 'bg-[var(--bg-hover)] border border-[var(--border-md)]'}`}
    >
      <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-5' : 'left-1'}`} />
    </button>
  );
}

function SettingsCard({ guildId }) {
  const [loaded, setLoaded] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [events, setEvents] = useState(DEFAULT_EVENTS);
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    Promise.allSettled([
      axios.get(`${API}/api/server-logs/${guildId}/settings`, authHeader()),
      axios.get(`${API}/api/announce/channels/${guildId}`, authHeader()),
    ]).then(([s, ch]) => {
      if (!alive) return;
      if (s.status === 'fulfilled') {
        const d = s.value.data || {};
        setChannelId(d.channel_id || '');
        setEvents({ ...DEFAULT_EVENTS, ...(d.events || {}) });
      } else {
        toast.error('Failed to load log settings');
      }
      if (ch.status === 'fulfilled') setChannels(ch.value.data.channels || []);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [guildId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/server-logs/${guildId}/settings`, { channel_id: channelId || null, events }, authHeader());
      toast.success('Log settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <h2 className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit] mb-4">
        <Settings2 size={16} className="text-[#5865F2]" /> Log settings
      </h2>
      {!loaded ? (
        <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading settings...</div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Log channel</label>
            <div className="relative">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <select
                value={channelId}
                onChange={e => setChannelId(e.target.value)}
                className="w-full appearance-none bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl pl-8 pr-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] focus:outline-none focus:border-[#5865F2]/50"
              >
                <option value="">Dashboard only</option>
                {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            {EVENT_KEYS.map(k => {
              const { label, icon: Icon, cls } = EVENTS[k];
              return (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm text-[var(--text-secondary)] font-[Manrope]">
                    <span className={`w-6 h-6 rounded-md border flex items-center justify-center ${cls}`}><Icon size={12} /></span>
                    {label}
                  </span>
                  <Toggle checked={!!events[k]} onChange={v => setEvents(prev => ({ ...prev, [k]: v }))} />
                </div>
              );
            })}
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} Save settings
          </button>
          <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Logs are kept for 30 days.</p>
        </div>
      )}
    </div>
  );
}

function LogRow({ log }) {
  const meta = EVENTS[log.event] || { label: log.event, icon: ScrollText, cls: 'text-[var(--text-muted)] bg-[var(--bg-hover)] border-[var(--border)]' };
  const Icon = meta.icon;
  const before = asText(log.before);
  const after = asText(log.after);
  const details = asText(log.details);
  const showDiff = before || after;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="flex gap-3 px-5 py-3.5"
    >
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${meta.cls}`}>
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[var(--text-primary)] text-sm font-semibold font-[Manrope]">{log.user_name || log.user_id || 'Unknown'}</span>
          <span className="text-[var(--text-secondary)] text-xs font-[Manrope]">{meta.label.toLowerCase()}</span>
          {log.channel_name && (
            <span className="text-[var(--text-muted)] text-xs font-[Manrope]">in #{log.channel_name}</span>
          )}
          <span className="text-[var(--text-muted)] text-[11px] font-[Manrope] ml-auto" title={log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}>
            {timeAgo(log.timestamp)}
          </span>
        </div>
        {details && (
          <p className="text-[var(--text-secondary)] text-sm font-[Manrope] whitespace-pre-wrap break-words mt-0.5">{details}</p>
        )}
        {showDiff && (
          <div className="mt-1.5 text-sm font-[Manrope] space-y-1">
            {before && (
              <p className="text-[var(--text-muted)] line-through whitespace-pre-wrap break-words">{before}</p>
            )}
            {before && after && <ArrowRight size={12} className="text-[var(--text-muted)]" />}
            {after && (
              <p className="text-[var(--text-primary)] whitespace-pre-wrap break-words">{after}</p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function ServerLogs() {
  const { selectedGuildId, selectedGuild } = useGuild();
  const isStaff = !!selectedGuild?.is_staff;
  const isGlobal = !!selectedGuild?.is_global;
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter !== 'all') params.set('event', filter);
      const r = await axios.get(`${API}/api/server-logs/${selectedGuildId}?${params}`, authHeader());
      setLogs(r.data.logs || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load server logs.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedGuildId, isGlobal, filter]);

  useEffect(() => {
    fetchLogs();
    const id = setInterval(fetchLogs, 15000);
    return () => clearInterval(id);
  }, [fetchLogs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="server-logs-main">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-[var(--text-primary)] flex items-center gap-3">
              <ScrollText size={28} className="text-[#5865F2]" />
              Server Logs
            </h1>
            <p className="text-[var(--text-muted)] text-sm mt-1 font-[Manrope]">Message edits, deletions, joins, leaves and member updates</p>
          </div>
          {!isGlobal && (
            <button
              onClick={fetchLogs}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
            </button>
          )}
        </div>

        {isGlobal ? (
          <div className="max-w-3xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
            <Server size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-[Manrope] text-sm">Select a specific server to view its logs</p>
          </div>
        ) : (
          <div className={`grid gap-6 ${isStaff ? 'xl:grid-cols-[1fr_320px]' : ''} max-w-6xl`}>
            <div className="space-y-4 min-w-0">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm font-[Manrope]">{error}</div>
              )}

              <div className="flex flex-wrap gap-2">
                {['all', ...EVENT_KEYS].map(k => {
                  const active = filter === k;
                  const meta = EVENTS[k];
                  const Icon = meta?.icon;
                  return (
                    <button
                      key={k}
                      onClick={() => setFilter(k)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium font-[Manrope] transition-colors ${
                        active
                          ? 'bg-[#5865F2] border-[#5865F2] text-white'
                          : 'bg-[var(--bg-surface)] border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {Icon && <Icon size={12} />}
                      {meta ? meta.chip : 'All'}
                    </button>
                  );
                })}
              </div>

              {logs.length === 0 ? (
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
                  <ScrollText size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
                  <p className="text-[var(--text-muted)] font-[Manrope] text-sm">No log entries yet</p>
                  <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">Events will appear here as they happen in your server</p>
                </div>
              ) : (
                <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl divide-y divide-[var(--border)] overflow-hidden">
                  {logs.map((log, i) => (
                    <LogRow key={`${log.timestamp}-${log.event}-${log.user_id}-${i}`} log={log} />
                  ))}
                </div>
              )}
              <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Auto-refreshes every 15 seconds · logs are kept for 30 days</p>
            </div>

            {isStaff && (
              <div>
                <SettingsCard guildId={selectedGuildId} />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
